import { useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';

import CollapsibleSidebarLayout from '../CollapsibleSidebarLayout';
import DashboardHeader from '../DashboardHeader';
import DashboardSidebarPublic from './DashboardSidebarPublic';

import { HaggleProductStatus, PriceRange } from '@/components/utils/enums';
import { useUser } from '@/hooks/useUser';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { FilterStateSideBar } from '@/interfaces/home/IFilterStateSideBar';

const DashboardLayoutPublic = () => {
	const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
	const [, setSearchParams] = useSearchParams();
	const { user } = useUser();

	const [filters, setFilters] = useState<FilterStateSideBar>({
		productTypes: [],
		vendors: [],
		priceRanges: [],
		statuses: [],
	});

	const toggleSidebar = () => {
		setIsSidebarOpen((prev) => !prev);
	};

	const handleFiltersChange = (newFilters: FilterStateSideBar) => {
		setFilters(newFilters);

		// Update URL search parameters to trigger product refetch
		const newSearchParams = new URLSearchParams();

		// Add filter parameters to URL
		if (newFilters.productTypes.length > 0) {
			newFilters.productTypes.forEach((type) => {
				newSearchParams.append('type', type);
			});
		}

		if (newFilters.vendors.length > 0) {
			newFilters.vendors.forEach((vendor) => {
				newSearchParams.append('vendor', vendor);
			});
		}

		if (newFilters.priceRanges.length > 0) {
			newFilters.priceRanges.forEach((range) => {
				switch (range) {
					case PriceRange.Under100:
						newSearchParams.append('minPrice', '0');
						newSearchParams.append('maxPrice', '100');
						break;
					case PriceRange.OneHundredToFiveHundred:
						newSearchParams.append('minPrice', '100');
						newSearchParams.append('maxPrice', '500');
						break;
					case PriceRange.FiveHundredToOneThousand:
						newSearchParams.append('minPrice', '500');
						newSearchParams.append('maxPrice', '1000');
						break;
					case PriceRange.OverOneThousand:
						newSearchParams.append('minPrice', '1000');
						break;
				}
			});
		}

		if (newFilters.statuses.length > 0) {
			// Only add status to URL if it's not "events"
			// "Events" is a client-side only filter that shows all products
			newFilters.statuses.forEach((status) => {
				if (status !== HaggleProductStatus.Events) {
					newSearchParams.append('status', status);
				}
			});
		}

		// Update URL without navigation
		setSearchParams(newSearchParams);
	};

	// Check if user is a consumer or guest (no user)
	const isConsumerOrGuest = !user || user.roles.includes(UserRole.Consumer);

	// For consumers and guests, render without sidebar layout
	if (isConsumerOrGuest) {
		return (
			<div className="h-screen">
				<div className="flex flex-1 flex-col overflow-x-hidden z-30 bg-gray-50">
					<DashboardHeader onMenuClick={toggleSidebar} />
					<main>
						<div className="mx-auto p-2 md:p-4">
							<Outlet context={{ filters, setFilters: handleFiltersChange }} />
						</div>
					</main>
				</div>
			</div>
		);
	}

	// For non-consumers, render with sidebar layout
	return (
		<CollapsibleSidebarLayout
			sidebar={<DashboardSidebarPublic onFiltersChange={handleFiltersChange} />}
			header={<DashboardHeader onMenuClick={toggleSidebar} />}
			isSidebarOpen={isSidebarOpen}
			toggleSidebar={toggleSidebar}
			contentBg="bg-grey-5"
		>
			<Outlet context={{ filters, setFilters: handleFiltersChange }} />
		</CollapsibleSidebarLayout>
	);
};

export default DashboardLayoutPublic;
